import React from 'react';
import {
  AlertOctagon, AlertTriangle, Ban, Check, ChevronRight, FileText, Info, Pencil,
  RotateCcw, RouteOff, UserCheck, Users, Zap,
} from 'lucide-react';
import Highlight from '../../../components/Highlight';
import {
  MISC_TASK_STATUS, MiscTaskHistoryEntry, MiscTaskQueue, MiscTaskRow as Row, MiscTaskViewer,
} from '../../../interfaces/MiscTaskApprovalInterface';
import {
  DerivedHistoryEntry, ageing, approvalsByLevel, approverNames, canDeny, canEditDescription,
  canForce, columnCount, formatDay, formatStamp, formatSubmittedDay, formatSubmittedStamp,
  formatSubmittedTime, fullName, groupByGeneration, hasNoApprover, isMine, isPendingQueue, money,
  supersededDescription, waitingNote, wasCorrectedAfterApproval, withGenerations,
} from './miscTaskHelpers';

/**
 * One red: the table row, plus the detail row that opens underneath it.
 *
 * The detail is a sibling <tr>, not a drawer — same gesture as the time-off
 * approvals page. Clicking anywhere on the main row toggles it; every
 * interactive cell stops propagation so a button click never also collapses the
 * row out from under the pointer.
 */

const MAX_DESCRIPTION = 60;

export interface RowHistoryState {
  loading: boolean;
  entries: MiscTaskHistoryEntry[] | null;
  error: string | null;
}

interface Props {
  row: Row;
  viewer: MiscTaskViewer | null;
  queue: MiscTaskQueue;
  term: string;
  now: Date;
  open: boolean;
  flash: boolean;
  history: RowHistoryState | undefined;
  draft: string;
  comment: string;
  commentOpen: boolean;
  error: { headline: string; body: string } | undefined;
  busy: boolean;
  onToggle: (id: number) => void;
  onDraftChange: (id: number, value: string) => void;
  onCommentChange: (id: number, value: string) => void;
  onCommentToggle: (id: number) => void;
  onApprove: (row: Row, level: 1 | 2) => void;
  onDeny: (row: Row) => void;
  onForce: (row: Row) => void;
  onEditDescription: (row: Row) => void;
}

const ACTION_META: Record<string, { title: string; cls: string; icon: React.ReactNode }> = {
  APPROVE: { title: 'Approved', cls: 'a-approve', icon: <Check size={14} /> },
  FORCE_APPROVE: { title: 'Force-approved', cls: 'a-force', icon: <Zap size={14} /> },
  DENY: { title: 'Denied', cls: 'a-deny', icon: <Ban size={14} /> },
  REOPEN: { title: 'Reopened by an edit', cls: 'a-reopen', icon: <RotateCcw size={14} /> },
  DESCRIPTION_EDIT: { title: 'Description corrected', cls: 'a-edit', icon: <Pencil size={14} /> },
};

const levelLabel = (level: number | null): string => (level === 1 ? '1st' : level === 2 ? '2nd' : '');

const HistoryRow: React.FC<{ entry: DerivedHistoryEntry }> = ({ entry }) => {
  const meta = ACTION_META[entry.action] ?? { title: entry.action, cls: 'a-edit', icon: <Info size={14} /> };
  return (
    <div className={`hrow ${meta.cls}`}>
      <div className="hi">{meta.icon}</div>
      <div className="hbody">
        <div className="ht">
          {meta.title}
          {entry.approvalLevel ? ` · ${levelLabel(entry.approvalLevel)} level` : ''}
        </div>
        {/* A null adminEmployeeID is the EMPLOYEE editing from mobile, not a
            missing admin — the server has already resolved the name. */}
        <div className="hw">by <b>{entry.actorName}</b>{entry.byEmployee ? ' (employee)' : ''}</div>
        <div className="hwhen">{formatStamp(entry.actionDateTime, entry.actionDateTimeOffset)}</div>
        {entry.comments && <div className="hc">{entry.comments}</div>}
        {entry.superDescriptionSnapshot && (
          <div className="hs">
            {entry.superDescriptionSnapshot}
            {entry.previousSuperDescription && (
              <span className="wasq"> (was &ldquo;{entry.previousSuperDescription}&rdquo;)</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MiscTaskRowView: React.FC<Props> = ({
  row, viewer, queue, term, now, open, flash, history, draft, comment, commentOpen, error, busy,
  onToggle, onDraftChange, onCommentChange, onCommentToggle, onApprove, onDeny, onForce, onEditDescription,
}) => {
  const id = row.miscEmployeeTaskID;
  const mine = isMine(row);
  const pending = isPendingQueue(queue);
  const age = ageing(row.pendingSince, now);
  const entries = history?.entries ?? null;
  const derived = entries ? withGenerations(entries, row.generation) : [];
  // Clicking a control must never also collapse the row out from under it.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // ── shared leading cells ───────────────────────────────────────────────────

  const chevronCell = (
    <td className="c-exp">
      <button type="button" className="mtb-chev" tabIndex={-1} aria-hidden>
        <ChevronRight size={16} />
      </button>
    </td>
  );

  const employeeCell = (
    <td className="c-emp">
      <div className="nm">
        <span><Highlight text={fullName(row.name)} term={term} /></span>
        {row.generation > 1 && row.approvalStatus < MISC_TASK_STATUS.approved && (
          <span
            className="mtb-chip reopen"
            title="Approved once, then edited — the chain restarted and the Vista description was cleared"
          >
            <RotateCcw size={11} />Reopened
          </span>
        )}
      </div>
      <div className="sub">
        #<Highlight text={row.altEmployeeID} term={term} />{' · '}
        {row.supervisor
          ? fullName(row.supervisor.name)
          : <span className="nosup">no supervisor</span>}
      </div>
    </td>
  );

  const jobCell = (
    <td className="c-job">
      <div className="jn">
        <Highlight text={row.project?.name} term={term} />{' '}
        <span className="num"><Highlight text={row.project?.altProjectID} term={term} /></span>
      </div>
      {row.altIntermediateID ? (
        <div className="ph">
          <Highlight text={row.altIntermediateID} term={term} />
          {row.intermediateName ? <> · <Highlight text={row.intermediateName} term={term} /></> : null}
        </div>
      ) : (
        <span className="nophase"><AlertTriangle size={11} />No phase</span>
      )}
    </td>
  );

  // SUBMITTED, not the work date. `entryDate` is stamped once per pay period at
  // NPM, so as a column it was a constant; the submit stamp is what shows the
  // 5-6-days-after-the-period-closed entry the client is chasing. The ageing
  // sub-line is untouched — it is the urgency signal the queue is scanned for.
  //
  // The time is compact ("2:14p") and secondary on purpose: the date stays the
  // scannable value, and the full stamp is spelled out in the detail row below.
  const submittedCell = (
    <td className="c-when">
      {formatSubmittedDay(row.submittedDateTime)}
      {formatSubmittedTime(row.submittedDateTime) && (
        <span className="tm"> · {formatSubmittedTime(row.submittedDateTime)}</span>
      )}
      {age && <span className={`age${age.hot ? ' hot' : ''}`}>{age.text}</span>}
    </td>
  );

  const amountCell = (
    <td className={`c-amt${row.approvalStatus === MISC_TASK_STATUS.denied ? ' zeroed' : ''}`}>
      {money(row.amountClaimed)}
      {row.originalAmountClaimed != null && <span className="was">{money(row.originalAmountClaimed)}</span>}
    </td>
  );

  // ── the optional approver comment (D22) ────────────────────────────────────
  // The contract has always accepted `comments` on approve; it is collapsed
  // behind an affordance so it reads optional rather than skipped-required.

  const commentField = (
    <div className="mtb-cmt" onClick={stop}>
      <button
        type="button"
        className={`cmt-toggle${commentOpen ? ' on' : ''}`}
        onClick={() => onCommentToggle(id)}
      >
        <FileText size={12} />
        {commentOpen ? 'Hide comment' : comment.trim() ? 'Comment added' : 'Add a comment'}
      </button>
      {commentOpen && (
        <>
          <input
            type="text"
            value={comment}
            maxLength={255}
            placeholder="Optional — kept on the approval record"
            onChange={(e) => onCommentChange(id, e.target.value)}
          />
          <div className="cmt-hint">Only the approval trail sees this. It never reaches Vista.</div>
        </>
      )}
    </div>
  );

  const errorLine = error && (
    <div className="mtb-errline">
      <AlertOctagon size={14} />
      <div><b>{error.headline}</b> {error.body}</div>
    </div>
  );

  const extraButtons = (
    <>
      {canForce(row, viewer) && (
        <button type="button" className="mtb-btn xs force" disabled={busy} onClick={() => onForce(row)}>
          <Zap size={14} />Force
        </button>
      )}
      {canDeny(row, viewer) && (
        <button type="button" className="mtb-btn xs deny-outline" disabled={busy} onClick={() => onDeny(row)}>
          <Ban size={14} />Deny
        </button>
      )}
    </>
  );

  // ── the action cell, pending buckets ───────────────────────────────────────
  // No bulk approve at either level (D19) and no Remind button (D21 — it was a
  // toast-only stub with no endpoint).

  const actionCell = (() => {
    if (!mine) {
      return (
        <td className="c-act" onClick={stop}>
          <div className="mtb-acts">{extraButtons}</div>
          {row.approvalStatus === MISC_TASK_STATUS.secondPending && (
            <div className="mtb-waitnote">2nd approver writes the Vista text</div>
          )}
          {errorLine}
        </td>
      );
    }

    if (row.approvalStatus === MISC_TASK_STATUS.firstPending) {
      return (
        <td className="c-act" onClick={stop}>
          <div className="mtb-acts">
            <button
              type="button"
              className="mtb-btn xs approve-solid"
              disabled={busy}
              onClick={() => onApprove(row, 1)}
            >
              <Check size={14} />Approve
            </button>
            {extraButtons}
          </div>
          {commentField}
          {errorLine}
        </td>
      );
    }

    const n = draft.length;
    return (
      <td className="c-act" onClick={stop}>
        <div className="mtb-comp">
          <div className="cin">
            <input
              type="text"
              maxLength={MAX_DESCRIPTION}
              value={draft}
              placeholder="Vista description — required"
              aria-label="Vista description"
              disabled={busy}
              onChange={(e) => onDraftChange(id, e.target.value)}
            />
            <span className={`count${n >= MAX_DESCRIPTION ? ' full' : n >= 50 ? ' near' : ''}`}>
              {n}/{MAX_DESCRIPTION}
            </span>
          </div>
          <div className="row">
            <button
              type="button"
              className="mtb-btn xs approve-solid"
              // Deliberately NOT disabled on an empty draft. The page's
              // handleApprove pops "Vista description required" instead, so the
              // requirement is stated rather than silently enforced.
              disabled={busy}
              onClick={() => onApprove(row, 2)}
            >
              <Check size={14} />Approve
            </button>
            {extraButtons}
          </div>
        </div>
        {commentField}
        {errorLine}
      </td>
    );
  })();

  // ── bucket-specific trailing cells ─────────────────────────────────────────

  const waitingCell = (() => {
    if (hasNoApprover(row)) {
      return (
        <td className="c-stat">
          <span className="mtb-chip noap"><RouteOff size={11} />No approver</span>
          <span className="line">
            Routing found nobody{viewer?.canForceApprove ? ' — force-approve to clear it' : ' — payroll can force it through'}
          </span>
        </td>
      );
    }
    const note = waitingNote(row);
    return (
      <td className="c-stat">
        {mine ? (
          <>
            <span className="mtb-chip mine"><UserCheck size={11} />You</span>
            {row.actingFor && (
              <span className="line">for <b>{row.actingFor.name}</b> (delegated)</span>
            )}
          </>
        ) : (
          row.pendingApprovers.map((p, i) => (
            <React.Fragment key={p.approvalGroupID ?? p.employeeID ?? i}>
              {i > 0 && ' and '}
              {p.approvalGroupName
                ? <span className="grp"><Users size={11} />{p.approvalGroupName}</span>
                : <b>{p.name}</b>}
            </React.Fragment>
          ))
        )}
        {note && <span className="line">{note}</span>}
      </td>
    );
  })();

  const approvedCells = (() => {
    const { first, second } = approvalsByLevel(entries ?? []);
    const editable = canEditDescription(row, viewer);
    return (
      <>
        <td className="c-desc"><Highlight text={row.miscDescription} term={term} /></td>
        <td className="c-desc" onClick={editable ? stop : undefined}>
          {editable ? (
            <button
              type="button"
              className="mtb-vistabox"
              title="Edit the Vista description — a correction, not an approval"
              onClick={() => onEditDescription(row)}
            >
              <span className="vt"><Highlight text={row.superDescription} term={term} /></span>
              <Pencil size={13} />
            </button>
          ) : (
            <div className="vista"><Highlight text={row.superDescription} term={term} /></div>
          )}
          {entries && wasCorrectedAfterApproval(entries) && (
            <div className="rl">corrected after approval</div>
          )}
        </td>
        <td className="c-stat">
          {first && (
            <span className="line">
              <span className="lv">1st</span><b>{first.actorName}</b> {formatDay(first.actionDateTime)}
            </span>
          )}
          {second && (
            <span className="line">
              <span className="lv">2nd</span><b>{second.actorName}</b> {formatDay(second.actionDateTime)}
            </span>
          )}
          {/* Until the trail arrives, the row still knows who acted last. */}
          {!first && !second && row.lastAction && (
            <span className="line">
              <span className="lv">{levelLabel(row.lastAction.level) || '—'}</span>
              <b>{row.lastAction.adminName}</b> {formatDay(row.lastAction.actionDateTime)}
            </span>
          )}
        </td>
        <td className="c-act" onClick={stop}>
          <div className="mtb-acts">
            {canDeny(row, viewer) && (
              <button type="button" className="mtb-btn xs deny-outline" disabled={busy} onClick={() => onDeny(row)}>
                <Ban size={14} />Deny
              </button>
            )}
          </div>
          {errorLine}
        </td>
      </>
    );
  })();

  const deniedCells = (
    <>
      <td className="c-desc"><div className="reason"><Highlight text={row.denyReason} term={term} /></div></td>
      <td className="c-stat">
        <b>{row.deniedByName ?? '—'}</b>
        <span className="line">{formatDay(row.deniedDateTime)} · not blocking their timesheet</span>
      </td>
      <td className="c-act" />
    </>
  );

  // ── the detail row ─────────────────────────────────────────────────────────

  const detail = (() => {
    const notes: React.ReactNode[] = [];
    if (hasNoApprover(row)) {
      notes.push(
        <div className="note block" key="noapprover">
          <RouteOff size={15} />
          <div>
            <b>Routing resolved to nobody.</b> No approver was found for this job and phase, so nobody can
            approve it and {row.name.first} cannot submit a timesheet.{' '}
            {viewer?.canForceApprove ? 'Force-approve clears it.' : 'Payroll can force it through.'}
          </div>
        </div>
      );
    }
    if (row.generation > 1 && row.approvalStatus < MISC_TASK_STATUS.approved) {
      const wiped = supersededDescription(derived, row.generation);
      notes.push(
        <div className="note reopen" key="reopen">
          <RotateCcw size={15} />
          <div>
            <b>Approved once already, then edited.</b> {row.lastAction?.adminName ?? 'Someone'} changed it
            {row.lastAction?.comments ? ` — ${row.lastAction.comments}` : ''}, which reset the chain and
            cleared the Vista description
            {wiped ? <> (it read <span className="was">{wiped}</span>)</> : null}. Routing was recalculated,
            so the approvers may not be the same people as last time.
          </div>
        </div>
      );
    }

    const chains = groupByGeneration(derived);
    const waitingOn = approverNames(row);

    return (
      <div className="mtb-dwrap">
        <div className="dmain">
          {notes}
          <div className="blk">
            <div className="dl">{row.name.first}&apos;s description</div>
            <div className="dv quote"><Highlight text={row.miscDescription} term={term} /></div>
          </div>
          <div className="grid2">
            <div>
              <div className="dl">Job</div>
              <div className="dv">
                <Highlight text={row.project?.name} term={term} />
                <div className="dv mono"><Highlight text={row.project?.altProjectID} term={term} /></div>
              </div>
            </div>
            <div>
              <div className="dl">Phase</div>
              <div className="dv mono">
                {row.altIntermediateID ? (
                  <>
                    <Highlight text={row.altIntermediateID} term={term} />
                    <br />
                    <Highlight text={row.intermediateName} term={term} />
                  </>
                ) : (
                  <span className="bad">none — this is what breaks the Vista export</span>
                )}
              </div>
            </div>
            <div>
              <div className="dl">Employee</div>
              <div className="dv">
                {fullName(row.name)}
                <div className="dv mono">#{row.altEmployeeID}</div>
              </div>
            </div>
            <div>
              <div className="dl">Supervisor</div>
              <div className="dv">
                {row.supervisor ? fullName(row.supervisor.name) : <span className="bad">none on record</span>}
              </div>
            </div>
            {/* Work date was here and is deliberately gone. `entryDate` is stamped
                once per pay period — 2,489 of 2,490 NPM reds sit exactly on the
                period start — so it said the same thing on every row and read as
                real information. It still reaches Vista and payroll from the API
                field; it is simply not worth a line on screen. */}
            {/* The exact submit time, spelled out — the column only has room for
                "Aug 8 · 2:14p", and this is where the precise value lives. */}
            <div>
              <div className="dl">Submitted</div>
              <div className="dv">
                {formatSubmittedStamp(row.submittedDateTime, row.submittedDateTimeOffset) || '—'}
              </div>
            </div>
            <div>
              <div className="dl">Amount</div>
              <div className="dv mono">
                {money(row.amountClaimed)}
                {row.originalAmountClaimed != null && (
                  <span className="mtb-muted"> (claimed {money(row.originalAmountClaimed)})</span>
                )}
              </div>
            </div>
            {row.superDescription && (
              <div className="wide">
                <div className="dl">Vista description</div>
                <div className="dv mono"><Highlight text={row.superDescription} term={term} /></div>
              </div>
            )}
            {row.denyReason && (
              <div className="wide">
                <div className="dl">Deny reason</div>
                <div className="dv">
                  <Highlight text={row.denyReason} term={term} />
                  <span className="mtb-muted"> — {row.deniedByName}, {formatStamp(row.deniedDateTime)}</span>
                </div>
              </div>
            )}
            {age && (
              <div className="wide">
                <div className="dl">Waiting</div>
                <div className="dv">
                  {age.text} at {levelLabel(row.pendingLevel) || '1st'} approval
                  {waitingOn ? ` · ${waitingOn}` : ''}
                </div>
              </div>
            )}
            <div className="wide">
              <div className="dl">Record</div>
              <div className="dv mono">miscEmployeeTaskID {id} · chain {row.generation}</div>
            </div>
          </div>
        </div>
        <div className="dside">
          <div className="dl">Approval history</div>
          <div className="hist">
            {history?.loading && <div className="none">Loading the trail…</div>}
            {history?.error && <div className="none">{history.error}</div>}
            {entries && entries.length === 0 && (
              <div className="none">
                Nothing has happened yet — entered by {row.name.first} on {formatDay(row.entryDate)}.
              </div>
            )}
            {chains.map((chain, i) => (
              <React.Fragment key={chain.generation}>
                {chains.length > 1 && (
                  <div className="genline">
                    Chain {chain.generation}
                    <span className="tag">{i === 0 ? 'current' : 'superseded by an edit'}</span>
                    <span className="rule" />
                  </div>
                )}
                {chain.entries.map((e) => (
                  <HistoryRow key={e.miscEmployeeTaskApprovalID} entry={e} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  })();

  const rowClass = [
    'mtb-main',
    mine ? 'act' : '',
    error ? 'err' : '',
    open ? 'open' : '',
    flash ? 'flash' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <tr id={`mtb-row-${id}`} className={rowClass} onClick={() => onToggle(id)}>
        {chevronCell}
        {employeeCell}
        {jobCell}
        {submittedCell}
        {amountCell}
        {pending && (
          <>
            <td className="c-desc"><Highlight text={row.miscDescription} term={term} /></td>
            {waitingCell}
            {actionCell}
          </>
        )}
        {queue === 'approved' && approvedCells}
        {queue === 'denied' && deniedCells}
      </tr>
      {open && (
        <tr className="mtb-det">
          <td colSpan={columnCount(queue)}>{detail}</td>
        </tr>
      )}
    </>
  );
};

export default MiscTaskRowView;
